import {Trans} from '@lingui/macro';
import React, {FC} from 'react';

import pluginsBannerImgSrc from '../../../assets/images/appsStorePluginsBanner.jpg';
import {ApplicationCardSkeleton} from '../../../components/common/applications/skeleton/applicationCardSkeleton';
import {IntegrationTypeModel} from '../../../models/api/integrationTypesModel';
import {SettingsAppsPluginsUrl} from '../../../models/url/settingsUrlModel';
import {AppsStoreBanner} from '../banner/appsStoreBanner';
import {AppsGrid} from '../layout/appsGrid';
import {SettingsAppCardContainer} from '../settingsAppCardContainer';
import {renderMultiLoadingState} from './settingsAppsPageHelpers';

/*
 * Constants.
 */

const maxIntegrationsToRender = 4;

/*
 * Props.
 */

interface SettingsAppsPluginsPageProps {
  pluginIntegrationTypes: ReadonlyArray<IntegrationTypeModel>;
  popularPluginIntegrationTypes: ReadonlyArray<IntegrationTypeModel>;
  recentlyAddedPluginIntegrationTypes: ReadonlyArray<IntegrationTypeModel>;
  isIntegrationTypesLoading: boolean;
  url: SettingsAppsPluginsUrl;
}

/*
 * Component.
 */

export const SettingsAppsPluginsPage: FC<SettingsAppsPluginsPageProps> = (props) => {
  const {
    pluginIntegrationTypes,
    popularPluginIntegrationTypes,
    recentlyAddedPluginIntegrationTypes,
    isIntegrationTypesLoading,
    url,
  } = props;

  return (
    <>
      <AppsStoreBanner
        backgroundUrl={pluginsBannerImgSrc}
        title={
          <Trans id="settings-apps-plugins-banner-title">Everything you need, right where you need it</Trans>
        }
        description={
          <Trans id="settings-apps-plugins-banner-description">
            Plugins bring customer context and key actions right into your inbox. Once a plugin is enabled, it
            can be easily accessed from the right sidebar of your Front inbox.
          </Trans>
        }
      />
      <AppsGrid
        title={<Trans id="settings-apps-plugins-popular-title">Popular</Trans>}
        isLoading={isIntegrationTypesLoading}
        renderLoadingState={renderSingleLineLoadingState}
        shouldHideOverflowingItems
      >
        {popularPluginIntegrationTypes.slice(0, maxIntegrationsToRender).map((integrationType) => (
          <SettingsAppCardContainer key={integrationType.id} integrationType={integrationType} url={url} />
        ))}
      </AppsGrid>
      <AppsGrid
        title={<Trans id="settings-apps-plugins-recently-added-title">Recently added</Trans>}
        isLoading={isIntegrationTypesLoading}
        renderLoadingState={renderSingleLineLoadingState}
        shouldHideOverflowingItems
      >
        {recentlyAddedPluginIntegrationTypes.slice(0, maxIntegrationsToRender).map((integrationType) => (
          <SettingsAppCardContainer key={integrationType.id} integrationType={integrationType} url={url} />
        ))}
      </AppsGrid>
      <AppsGrid
        title={<Trans id="settings-apps-plugins-all-title">All</Trans>}
        isLoading={isIntegrationTypesLoading}
        renderLoadingState={renderMultiLoadingState}
        shouldHaveLoadingGradient
      >
        {pluginIntegrationTypes.map((integrationType) => (
          <SettingsAppCardContainer key={integrationType.id} integrationType={integrationType} url={url} />
        ))}
      </AppsGrid>
    </>
  );
};

/*
 * Helpers.
 */

function renderSingleLineLoadingState() {
  return (
    <>
      <ApplicationCardSkeleton hasButton />
      <ApplicationCardSkeleton hasButton />
      <ApplicationCardSkeleton hasButton />
      <ApplicationCardSkeleton hasButton />
    </>
  );
}
